import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Send, 
  MessageCircle, 
  MessageSquare,
  AlertCircle,
  Package,
  Calendar,
  User
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const ComposeMessage = ({ 
  isOpen, 
  onClose, 
  onSend, 
  users, 
  taskMessage = null,
  productId = null,
  stage = null 
}) => {
  const [formData, setFormData] = useState({
    tagged_users: [],
    subject: '',
    content: '',
    message_type: 'direct',
    task_action: null
  });
  const [taskTemplates, setTaskTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const taskActions = [
    {
      value: 'please_comment',
      label: 'Please Comment',
      icon: MessageSquare,
      color: 'text-blue-600'
    },
    {
      value: 'please_progress',
      label: 'Please Progress This',
      icon: AlertCircle,
      color: 'text-orange-600'
    },
    {
      value: 'please_order',
      label: 'Please Order Items',
      icon: Package,
      color: 'text-purple-600'
    },
    {
      value: 'schedule_meeting',
      label: 'Schedule Meeting',
      icon: Calendar,
      color: 'text-green-600'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchTaskTemplates();
      
      // Pre-fill if this is a task message
      if (taskMessage) {
        setFormData(prev => ({
          ...prev,
          tagged_users: taskMessage.recipient_id ? [taskMessage.recipient_id] : [],
          subject: taskMessage.subject || '',
          content: taskMessage.content || '',
          message_type: 'task_request',
          task_action: taskMessage.task_action || null
        }));
      } else {
        // Reset form for new message
        setFormData({
          tagged_users: [],
          subject: '',
          content: '',
          message_type: 'direct',
          task_action: null
        });
      }
    }
  }, [isOpen, taskMessage]);

  const fetchTaskTemplates = async () => {
    try {
      const response = await axios.get(`${API}/messages/task-templates`);
      setTaskTemplates(response.data);
    } catch (error) {
      console.error('Error fetching task templates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskActionChange = (taskAction) => {
    if (taskAction && taskTemplates[taskAction]) {
      const template = taskTemplates[taskAction];
      setFormData(prev => ({
        ...prev,
        task_action: taskAction,
        message_type: 'task_request',
        subject: template.subject,
        content: template.template
          .replace('{recipient_name}', prev.tagged_users.length > 0 ? getUserById(prev.tagged_users[0])?.full_name || '[Recipient Name]' : '[Recipient Name]')
          .replace('{product_name}', '[Product Name]')
          .replace('{stage}', stage ? stage.replace('_', ' ') : '[Stage]')
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        task_action: taskAction,
        message_type: taskAction ? 'task_request' : 'direct'
      }));
    }
  };

  const getUserById = (userId) => {
    return users.find(user => user.id === userId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.subject.trim()) {
      toast({
        title: "Error",
        description: "Subject is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.content.trim()) {
      toast({
        title: "Error",
        description: "Message content is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.tagged_users.length === 0) {
      toast({
        title: "Error",
        description: "At least one recipient is required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const messageData = {
        ...formData,
        product_id: productId,
        stage: stage
      };

      await onSend(messageData);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get current user to exclude from recipients
  const currentUser = JSON.parse(localStorage.getItem('exled_user') || '{}');

  // Filter out current user from recipient list
  const availableUsers = users.filter(user => user.id !== currentUser.id);

  const selectedRecipients = formData.tagged_users.map(id => getUserById(id)).filter(Boolean);
  const selectedTaskAction = taskActions.find(action => action.value === formData.task_action);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Send className="w-5 h-5 text-teal-600" />
            <span>{taskMessage ? 'Send Task Message' : 'Compose Message'}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Recipient Selection */}
          <div className="space-y-2">
            <Label>To *</Label>
            <div className="border rounded-md p-2 min-h-[100px] max-h-[200px] overflow-y-auto">
              {availableUsers.length > 0 ? (
                availableUsers.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      id={user.id}
                      checked={formData.tagged_users.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleInputChange('tagged_users', [...formData.tagged_users, user.id])
                        } else {
                          handleInputChange('tagged_users', formData.tagged_users.filter(id => id !== user.id))
                        }
                      }}
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <label htmlFor={user.id} className="flex items-center space-x-2 cursor-pointer flex-1">
                      <User className="w-4 h-4 text-slate-500" />
                      <span>{user.full_name}</span>
                      <span className="text-xs text-slate-500">@{user.username}</span>
                    </label>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center p-8 text-slate-500">
                  <div className="text-center">
                    <User className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No other users available to message</p>
                    <p className="text-xs mt-1">You cannot send messages to yourself</p>
                  </div>
                </div>
              )}
            </div>
            {formData.tagged_users.length > 0 && (
              <div className="text-sm text-slate-600">
                Selected: {formData.tagged_users.length} user(s)
              </div>
            )}
          </div>

          {/* Task Action Selection (if applicable) */}
          {!taskMessage && (
            <div className="space-y-2">
              <Label>Message Type</Label>
              <Select
                value={formData.task_action || 'direct'}
                onValueChange={(value) => handleTaskActionChange(value === 'direct' ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select message type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="w-4 h-4 text-slate-500" />
                      <span>Regular Message</span>
                    </div>
                  </SelectItem>
                  {taskActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <SelectItem key={action.value} value={action.value}>
                        <div className="flex items-center space-x-2">
                          <Icon className={`w-4 h-4 ${action.color}`} />
                          <span>{action.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedTaskAction && (
                <p className="text-xs text-slate-500">
                  This will send a task request with email notification
                </p>
              )}
            </div>
          )}

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter message subject"
              required
            />
          </div>

          {/* Message Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Message *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Type your message here..."
              className="min-h-[120px]"
              required
            />
          </div>

          {/* Email Notification Info */}
          {selectedRecipients.length > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 text-blue-700">
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Email Notifications</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {selectedRecipients.filter(r => r.email_notifications).length} of {selectedRecipients.length} recipient(s) will receive email notifications
              </p>
            </div>
          )}

          {/* Product Context (if applicable) */}
          {(productId || stage) && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center space-x-2 text-slate-700">
                <Package className="w-4 h-4" />
                <span className="text-sm font-medium">Product Context</span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                This message is related to a product
                {stage && ` (${stage.replace('_', ' ')} stage)`}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || formData.tagged_users.length === 0 || !formData.subject || !formData.content}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
            >
              {loading ? (
                <>
                  <div className="spinner w-4 h-4 mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeMessage;